import { motion } from "framer-motion";
import {
    Brain,
    Eye,
    BarChart3,
    Scissors,
    Code2,
    ShieldCheck,
    ArrowUpRight,
} from "lucide-react";
import { useLanguage } from "@/components/sybrax/LanguageProvider";
import { content } from "@/components/sybrax/i18n";

const productMeta = {
    mooddy: { icon: Brain },
    useen: { icon: Eye },
    "work-analyse": { icon: BarChart3 },
    abonelikiptal: { icon: Scissors },
    codupp: { icon: Code2 },
    promlock: { icon: ShieldCheck },
};

const toneFor = (statusKey) =>
    statusKey === "ready" ? "emerald" : statusKey === "inDevelopment" ? "amber" : "violet";

const toneClasses = {
    emerald:
        "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 dark:border-emerald-400/20",
    amber:
        "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30 dark:border-amber-400/20",
    violet:
        "bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30 dark:border-violet-400/20",
};

const dotClasses = {
    emerald: "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]",
    amber: "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.8)]",
    violet: "bg-violet-400 shadow-[0_0_8px_rgba(167,139,250,0.8)]",
};

const Products = () => {
    const { lang } = useLanguage();
    const c = content[lang].products;

    return (
        <section
            id="products"
            data-testid="products-section"
            className="relative py-24 sm:py-32 overflow-hidden"
        >
            <div className="pointer-events-none absolute top-1/4 -right-40 w-[480px] h-[480px] rounded-full bg-blue-600/[0.08] blur-[120px]" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="max-w-3xl"
                >
                    <span className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.25em] text-cyan-600 dark:text-cyan-400">
                        <span className="mr-3 h-px w-8 bg-cyan-500/70 dark:bg-cyan-400/60" />
                        {c.label}
                    </span>
                    <h2
                        data-testid="products-title"
                        className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white"
                    >
                        {c.title}
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                        {c.intro}
                    </p>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8 items-stretch">
                    {c.items.map((p, i) => {
                        const Icon = productMeta[p.slug].icon;
                        const tone = toneFor(p.statusKey);
                        return (
                            <motion.article
                                key={p.slug}
                                data-testid={`product-card-${p.slug}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.6,
                                    ease: [0.22, 1, 0.36, 1],
                                    delay: (i % 3) * 0.08,
                                }}
                                className="glass-card relative p-7 sm:p-8 flex flex-col h-full group"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 dark:border-cyan-400/20 flex items-center justify-center group-hover:bg-cyan-500/15 transition-colors">
                                            <Icon
                                                size={22}
                                                strokeWidth={1.5}
                                                className="text-cyan-600 dark:text-cyan-300"
                                            />
                                        </div>
                                        <h3
                                            data-testid={`product-name-${p.slug}`}
                                            className="font-display text-2xl font-bold text-slate-900 dark:text-white tracking-tight"
                                        >
                                            {p.name}
                                        </h3>
                                    </div>
                                    <span
                                        data-testid={`product-status-${p.slug}`}
                                        className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${toneClasses[tone]}`}
                                    >
                                        <span className={`w-1.5 h-1.5 rounded-full ${dotClasses[tone]}`} />
                                        {c.statuses[p.statusKey]}
                                    </span>
                                </div>

                                <p className="mt-6 font-display text-lg sm:text-xl font-medium text-slate-800 dark:text-slate-100 leading-snug">
                                    {p.title}
                                </p>
                                <p className="mt-4 text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed flex-1">
                                    {p.description}
                                </p>

                                {p.link ? (
                                    <a
                                        href={p.link.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`product-link-${p.slug}`}
                                        className="mt-6 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-cyan-600 hover:text-cyan-700 dark:text-cyan-300 dark:hover:text-cyan-200 transition-colors group/link"
                                    >
                                        <span className="border-b border-transparent group-hover/link:border-current pb-0.5 transition-colors">
                                            {p.link.label}
                                        </span>
                                        <ArrowUpRight
                                            size={15}
                                            strokeWidth={2}
                                            className="transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
                                        />
                                    </a>
                                ) : (
                                    <div className="mt-6 h-px w-12 bg-gradient-to-r from-cyan-500/70 dark:from-cyan-400/60 to-transparent group-hover:w-24 transition-all duration-500" />
                                )}
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Products;
